import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { usePopup } from "../../context/PopupContext";
import { useMaintenance } from "../../context/MaintenanceContext";
import { useTranslation } from "react-i18next";
import { FaCog, FaStore, FaBell, FaShieldAlt } from "react-icons/fa";
import api from "../../utils/api";
import "./AdminSettings.css";

const AdminSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const { checkMaintenance } = useMaintenance();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    siteName: "Taja Bazzar",
    siteDescription: "Fresh produce marketplace",
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: false,
    maxProductsPerFarmer: 50,
    minOrderAmount: 0,
    deliveryFee: 50,
    freeDeliveryThreshold: 500,
    currency: "NPR",
    timezone: "Asia/Kathmandu",
    sessionTimeout: 30,
    twoFactorAuth: true,
    strongPasswords: true,
    rateLimiting: false,
  });
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    productApproval: true,
    newUserRegistration: true,
    orderUpdates: true,
    systemAlerts: true,
  });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") navigate("/");
        else loadSettings();
      } catch {
        setLoading(false);
      }
    } else navigate("/login");
  }, [navigate]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/maintenance");
      setSettings((prev) => ({
        ...prev,
        maintenanceMode: data.maintenanceMode === true,
      }));
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const handleSave = () => {
    showToast(t("adminSettings.saveSuccess"), "success");
  };

  const handleMaintenanceToggle = async (enabled) => {
    try {
      await api.put("/admin/maintenance", { enabled });
      setSettings((prev) => ({ ...prev, maintenanceMode: enabled }));
      checkMaintenance();
      showToast(
        enabled
          ? t("adminSettings.maintenanceEnabled") || "Maintenance mode enabled"
          : t("adminSettings.maintenanceDisabled") ||
              "Maintenance mode disabled",
        enabled ? "warning" : "success",
      );
    } catch {
      showToast(
        t("adminSettings.maintenanceError") ||
          "Failed to update maintenance mode",
        "error",
      );
    }
  };

  const handleInput = (field, value) =>
    setSettings((prev) => ({ ...prev, [field]: value }));
  const handleNotif = (field, value) =>
    setNotifications((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <AdminLayout>
        <div className="as-loading">
          <div className="as-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("adminSettings.loading")}</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="as-page">
        <div className="as-head">
          <h1 className="as-title">{t("adminSettings.title")}</h1>
          <button className="as-save-all" onClick={handleSave}>
            {t("adminSettings.saveGeneral") || "Save All"}
          </button>
        </div>

        <div className="as-grid">
          {/* General */}
          <div className="as-card">
            <div className="as-card-header">
              <FaCog className="as-card-ico as-card-ico--general" />
              <h2>{t("adminSettings.generalSettings")}</h2>
            </div>
            <div className="as-form">
              <label>{t("adminSettings.siteName")}</label>
              <input
                type="text"
                value={settings.siteName}
                onChange={(e) => handleInput("siteName", e.target.value)}
                className="as-input"
              />

              <label>{t("adminSettings.siteDescription")}</label>
              <textarea
                value={settings.siteDescription}
                onChange={(e) => handleInput("siteDescription", e.target.value)}
                className="as-input as-textarea"
                rows="2"
              />

              <div className="as-row">
                <div className="as-field">
                  <label>{t("adminSettings.currency")}</label>
                  <select
                    value={settings.currency}
                    onChange={(e) => handleInput("currency", e.target.value)}
                    className="as-input"
                  >
                    <option value="NPR">NPR</option>
                    <option value="USD">USD</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
                <div className="as-field">
                  <label>{t("adminSettings.timezone")}</label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => handleInput("timezone", e.target.value)}
                    className="as-input"
                  >
                    <option value="Asia/Kathmandu">Asia/Kathmandu</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>

              <div className="as-toggles">
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.maintenanceMode")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={settings.allowRegistration}
                    onChange={(e) =>
                      handleInput("allowRegistration", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.allowRegistration")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={settings.requireEmailVerification}
                    onChange={(e) =>
                      handleInput("requireEmailVerification", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.requireEmailVerification")}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Business */}
          <div className="as-card">
            <div className="as-card-header">
              <FaStore className="as-card-ico as-card-ico--business" />
              <h2>{t("adminSettings.businessSettings")}</h2>
            </div>
            <div className="as-form">
              <div className="as-row">
                <div className="as-field">
                  <label>{t("adminSettings.maxProducts")}</label>
                  <input
                    type="number"
                    value={settings.maxProductsPerFarmer}
                    onChange={(e) =>
                      handleInput(
                        "maxProductsPerFarmer",
                        parseInt(e.target.value),
                      )
                    }
                    className="as-input"
                    min="1"
                  />
                </div>
                <div className="as-field">
                  <label>{t("adminSettings.minOrderAmount")}</label>
                  <input
                    type="number"
                    value={settings.minOrderAmount}
                    onChange={(e) =>
                      handleInput("minOrderAmount", parseFloat(e.target.value))
                    }
                    className="as-input"
                    min="0"
                  />
                </div>
              </div>
              <div className="as-row">
                <div className="as-field">
                  <label>{t("adminSettings.deliveryFee")}</label>
                  <input
                    type="number"
                    value={settings.deliveryFee}
                    onChange={(e) =>
                      handleInput("deliveryFee", parseFloat(e.target.value))
                    }
                    className="as-input"
                    min="0"
                  />
                </div>
                <div className="as-field">
                  <label>{t("adminSettings.freeDeliveryThreshold")}</label>
                  <input
                    type="number"
                    value={settings.freeDeliveryThreshold}
                    onChange={(e) =>
                      handleInput(
                        "freeDeliveryThreshold",
                        parseFloat(e.target.value),
                      )
                    }
                    className="as-input"
                    min="0"
                  />
                </div>
              </div>
              <p className="as-hint">{t("adminSettings.freeDeliveryHelp")}</p>
            </div>
          </div>

          {/* Notifications */}
          <div className="as-card">
            <div className="as-card-header">
              <FaBell className="as-card-ico as-card-ico--notif" />
              <h2>{t("adminSettings.notificationSettings")}</h2>
            </div>
            <div className="as-form">
              <div className="as-toggles">
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={notifications.emailNotifications}
                    onChange={(e) =>
                      handleNotif("emailNotifications", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.emailNotifications")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={notifications.productApproval}
                    onChange={(e) =>
                      handleNotif("productApproval", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.productApproval")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={notifications.newUserRegistration}
                    onChange={(e) =>
                      handleNotif("newUserRegistration", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.newUserRegistration")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={notifications.orderUpdates}
                    onChange={(e) =>
                      handleNotif("orderUpdates", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.orderUpdates")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={notifications.systemAlerts}
                    onChange={(e) =>
                      handleNotif("systemAlerts", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.systemAlerts")}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="as-card">
            <div className="as-card-header">
              <FaShieldAlt className="as-card-ico as-card-ico--security" />
              <h2>{t("adminSettings.securitySettings")}</h2>
            </div>
            <div className="as-form">
              <label>{t("adminSettings.sessionTimeout")}</label>
              <input
                type="number"
                value={settings.sessionTimeout}
                onChange={(e) =>
                  handleInput("sessionTimeout", parseInt(e.target.value))
                }
                className="as-input"
                min="5"
                max="1440"
              />
              <p className="as-hint">{t("adminSettings.sessionTimeoutHelp")}</p>

              <div className="as-toggles">
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={settings.twoFactorAuth}
                    onChange={(e) =>
                      handleInput("twoFactorAuth", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.twoFactorAuth")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={settings.strongPasswords}
                    onChange={(e) =>
                      handleInput("strongPasswords", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.strongPasswords")}</span>
                </label>
                <label className="as-toggle">
                  <input
                    type="checkbox"
                    checked={settings.rateLimiting}
                    onChange={(e) =>
                      handleInput("rateLimiting", e.target.checked)
                    }
                  />
                  <span className="as-switch" />
                  <span>{t("adminSettings.rateLimiting")}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
